import { GoogleLogin } from "@react-oauth/google";
import { jwtDecode } from "jwt-decode";
import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { postData } from "../Networking/Api";
import { logEvent } from "../ga";
import Loader2 from "./Loader2";

function AppointmentAuthentication() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    email: "",
  });
  const [verificationCode, setVerificationCode] = useState("");
  const [currentStep, setCurrentStep] = useState("email"); // "email", "verification", "success"
  const [apiError, setApiError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [generatedCode, setGeneratedCode] = useState("");
  const [countdown, setCountdown] = useState(0);
  const [timerActive, setTimerActive] = useState(false);

  // Effect to handle countdown timer
  useEffect(() => {
    let timer = null;
    if (timerActive && countdown > 0) {
      timer = setTimeout(() => {
        setCountdown(countdown - 1);
      }, 1000);
    } else if (countdown === 0) {
      setTimerActive(false);
    }
    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [countdown, timerActive]);

  // Start the countdown timer
  const startCountdown = () => {
    setCountdown(60);
    setTimerActive(true);
  };

  // Helper function to generate a random 6-digit code
  const generateVerificationCode = () => {
    return Math.floor(100000 + Math.random() * 900000).toString();
  };

  // Decode appointment data from URL
  const decodeAppointmentDataFromUrl = () => {
    const urlParams = new URLSearchParams(window.location.search);
    const encodedData = urlParams.get('data');

    if (encodedData) {
      try {
        const decodedString = atob(encodedData);
        const parsedData = JSON.parse(decodedString);
        return parsedData;
      } catch (err) {
        console.error('Failed to decode appointment data:', err);
      }
    }
    return null;
  };

  // Initialize email from URL data on component mount
  useEffect(() => {
    const appointmentData = decodeAppointmentDataFromUrl();
    if (appointmentData && appointmentData.email) {
      setFormData({ email: appointmentData.email });
    }
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    setErrors({ ...errors, [name]: "" });
    setApiError("");
    setSuccessMessage("");
  };

  const validateEmail = () => {
    let isValid = true;
    let errors = {};

    if (!formData.email.trim()) {
      isValid = false;
      errors.email = "Email is required.";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      isValid = false;
      errors.email = "Invalid email format.";
    }

    setErrors(errors);
    return isValid;
  };

  // Function to send verification code via API
  const sendVerificationCode = async (email) => {
    const code = generateVerificationCode();
    setGeneratedCode(code);

    try {
      const payload = {
        email: email,
        code: code
      };

      const response = await postData("demo/sentVerificationCode", payload);
      console.log('Verification code response:', response);

      setSuccessMessage(`Verification code sent to ${email}. Please check your inbox.`);
      return code;
    } catch (error) {
      console.error('Error sending verification code:', error);
      throw new Error('Failed to send verification code email. Please try again.');
    }
  };

  // Handle Google verification
  const handleGoogleVerification = async (response) => {
    setLoading(true);
    setApiError("");
    setSuccessMessage("");

    try {
      const decodedToken = jwtDecode(response.credential);
      console.log("Google User Details", decodedToken);

      const appointmentData = decodeAppointmentDataFromUrl();
      
      // Check if Google email matches the appointment email
      if (appointmentData && appointmentData.email && 
          decodedToken.email.toLowerCase() === appointmentData.email.toLowerCase()) {
        
        setFormData({ email: decodedToken.email });
        localStorage.setItem('verifiedEmail', decodedToken.email);
        localStorage.setItem('verificationMethod', 'google');
        
        setCurrentStep("success");
        setSuccessMessage(`Successfully verified with Google account: ${decodedToken.email}`);
        
        // Log the verification event
        logEvent("Appointment", "GoogleVerification", decodedToken.email);
        
        // Redirect to appointment management after 3 seconds
        setTimeout(() => {
          // You can redirect to appointment management page here
          navigate('/BookingHistory');
        }, 3000);
        
      } else {
        throw new Error('Google account email does not match the appointment email.');
      }
    } catch (err) {
      console.error('Error with Google verification:', err);
      setApiError(err.message || 'Failed to verify with Google. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Handle email verification code submission
  const handleSendVerificationCode = async (event) => {
    event.preventDefault();
    
    if (validateEmail()) {
      setLoading(true);
      try {
        await sendVerificationCode(formData.email);
        localStorage.setItem('emailForVerification', formData.email);
        setCurrentStep("verification");
        startCountdown();
        logEvent("Appointment", "EmailVerificationSent", formData.email);
      } catch (error) {
        setApiError(error.message);
      } finally {
        setLoading(false);
      }
    }
  };

  // Handle verification code submission
  const handleVerificationSubmit = async (event) => {
    event.preventDefault();
    setLoading(true);
    setApiError("");

    try {
      if (verificationCode !== generatedCode) {
        throw new Error('Invalid verification code. Please check and try again.');
      }

      const savedEmail = localStorage.getItem('emailForVerification');
      if (!savedEmail) {
        throw new Error('Email not found. Please start the verification process again.');
      }

      localStorage.setItem('verifiedEmail', savedEmail);
      localStorage.setItem('verificationMethod', 'email');
      
      setCurrentStep("success");
      setSuccessMessage('Email verification successful! Redirecting to your appointment dashboard...');
      
      logEvent("Appointment", "EmailVerificationSuccess", savedEmail);

      // Redirect to appointment management after 3 seconds
      setTimeout(() => {
        navigate('/appointment-dashboard');
      }, 3000);

    } catch (err) {
      setApiError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Handle back to email step
  const handleBackToEmail = () => {
    setCurrentStep("email");
    setApiError("");
    setSuccessMessage("");
    setTimerActive(false);
    setCountdown(0);
  };

  // Handle resend verification code
  const handleResendVerification = async () => {
    setLoading(true);
    setApiError("");

    try {
      await sendVerificationCode(formData.email);
      startCountdown();
    } catch (err) {
      setApiError('Failed to resend verification code. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const renderEmailStep = () => (
    <>
      <div className="w-full text-center mb-6">
        <h2 className="font-fontPoppins text-xl font-semibold mb-2">Verification</h2>
        <p className="font-fontPoppins text-sm text-black px-4">
          Verify with Google for fast, 1-tap verification. If you change the email, then all future appointment updates will be sent to your new email upon verification.
        </p>
      </div>

      {/* Google Verification Button */}
      <div className="w-full text-center mb-4">
        <GoogleLogin 
          onSuccess={handleGoogleVerification} 
          onError={() => setApiError('Google verification failed')}
          size='large' 
          text="signin_with" 
          shape='pill'
          theme="outline"
        />
      </div>

      {/* OR Divider */}
      <div className="w-full flex items-center my-4">
        <div className="flex-grow border-t border-gray-300"></div>
        <span className="flex-shrink mx-4 text-black font-fontPoppins text-sm">or</span>
        <hr />
        <div className="flex-grow border-t border-gray-300"></div>
      </div>

      {/* Email Form */}
      <form onSubmit={handleSendVerificationCode}>
        <div className="w-full mb-4">
          <label className="w-full font-fontPoppins text-sm font-medium mb-1 ml-2 block">
            Email
          </label>
          <input
            className="w-full h-11 font-fontPoppins text-black-100 text-xs px-3 rounded-full shadow-sm focus:outline-none focus:ring-1 focus:ring-blueColor"
            type="email"
            name="email"
            value={formData.email}
            placeholder="Enter your email address"
            onChange={handleChange}
          />
          {errors.email && (
            <p className="text-red-500 text-sm mt-1 ml-2">{errors.email}</p>
          )}
        </div>

        <div className="w-full mb-4 px-2">
          <p className="font-fontPoppins text-xs text-black">
            We will send a verification code to your email. If you change the email, then all future appointment updates will be sent to your new email upon verification.
          </p>
        </div>

        <div className="w-full text-center">
          <button
            type="submit"
            disabled={loading}
            className="w-48 py-2 bg-blueColor rounded-full font-fontPoppins text-xs font-medium text-white hover:bg-opacity-80 disabled:opacity-50"
          >
            {loading ? 'Sending...' : 'Send Verification Code'} 
            <i className="fa-solid fa-arrow-right-long ms-1"></i>
          </button>
        </div>
      </form>
    </>
  );

  const renderVerificationStep = () => (
    <>
      <div className="w-full text-center mb-6">
        <h2 className="font-fontPoppins text-xl font-semibold mb-2">Enter Verification Code</h2>
        <p className="font-fontPoppins text-sm text-gray-600 px-4">
          We've sent a verification code to <strong>{formData.email}</strong>. Please check your email and enter the 6-digit code below.
        </p>
      </div>

      <form onSubmit={handleVerificationSubmit}>
        <div className="w-full mb-4">
          <label className="w-full font-fontPoppins text-sm font-medium mb-1 ml-2 block">
            Verification Code
          </label>
          <input
            className="w-full h-11 font-fontPoppins text-black-100 text-center text-lg px-3 rounded-full shadow-sm focus:outline-none focus:ring-1 focus:ring-blueColor tracking-widest"
            type="text"
            value={verificationCode}
            onChange={(e) => setVerificationCode(e.target.value.replace(/[^0-9]/g, '').slice(0, 6))}
            placeholder="123456"
            maxLength="6"
            required
          />
        </div>

        <div className="w-full flex justify-between items-center mb-4">
          <button
            type="button"
            onClick={handleBackToEmail}
            className="font-fontPoppins text-sm text-blueColor hover:underline"
          >
            ← Back to Email
          </button>
          
          <button
            type="submit"
            disabled={loading || verificationCode.length !== 6}
            className="w-32 py-2 bg-blueColor rounded-full font-fontPoppins text-xs font-medium text-white hover:bg-opacity-80 disabled:opacity-50"
          >
            {loading ? 'Verifying...' : 'Verify Code'}
          </button>
        </div>

        <div className="w-full text-center">
          {timerActive ? (
            <p className="font-fontPoppins text-sm text-gray-600">
              Resend available in {countdown} seconds
            </p>
          ) : (
            <button
              type="button"
              onClick={handleResendVerification}
              disabled={loading}
              className="font-fontPoppins text-sm text-blueColor hover:underline"
            >
              Resend verification code
            </button>
          )}
        </div>
      </form>
    </>
  );

  const renderSuccessStep = () => (
    <div className="w-full text-center">
      <div className="mb-4">
        <i className="fa-solid fa-check-circle text-green-500 text-4xl"></i>
      </div>
      <h2 className="font-fontPoppins text-xl font-semibold mb-2 text-green-600">
        Verification Successful!
      </h2>
      <p className="font-fontPoppins text-sm text-gray-600 mb-4">
        Your email has been verified successfully. Redirecting you to your appointment dashboard...
      </p>
      <div className="flex justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blueColor"></div>
      </div>
    </div>
  );

  return (
    <section className="w-full">
      {loading && <Loader2 />}
      <div className="w-full h-dvh flex items-center justify-center px-4 py-4">
        <div className="w-full max-w-md min-h-80 bg-gray-100 rounded-lg shadow-lg px-4 py-6">
          
          {/* Logo */}
          <div className="flex flex-direction-row justify-center gap-3 mb-6">
            <Link to="/">
              <img
                src={"https://storage.googleapis.com/samarawellprod/clinicLogo/a80cfbcd-87f3-44de-abc3-0534cbe84bd1.jpeg"}
                width="120px"
                alt="logo"
                className="max-w-full rounded-full"
              />
            </Link>
          </div>

          {/* Error Message */}
          {apiError && (
            <div className="w-full text-center mb-4">
              <p className="text-red-500 text-sm bg-red-50 p-2 rounded-lg">{apiError}</p>
            </div>
          )}

          {/* Success Message */}
          {successMessage && (
            <div className="w-full text-center mb-4">
              <p className="text-green-500 text-sm bg-green-50 p-2 rounded-lg">{successMessage}</p>
            </div>
          )}

          {/* Render Current Step */}
          {currentStep === "email" && renderEmailStep()}
          {currentStep === "verification" && renderVerificationStep()}
          {currentStep === "success" && renderSuccessStep()}
        </div>
      </div>
    </section>
  );
}

export default AppointmentAuthentication;